import "Components/Common/Modal/style.css";
import "Components/Core/BookingFormModule/new-booking-form.css";

import React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApiCall } from "Hooks/useApiCall";
import images from "Assets/images";
import { ls_vars } from "../../../Library/Constants";
import { getLocalStorage } from "../../../Network/ApiService";
import { uploadEquipment, uploadEquipmentUsage } from "Network/Core/EquipmentMaster/equipmentUsage";
import toast from "react-hot-toast";
import BulkErrorModal from "Components/Common/Modal/BulkErrorModal";

export interface MonthlyUsageModelProps {
    equipment?: number;
    project: number | string;
    region: number;
    handleClose?: () => void;
    type: string;
    onUploadSuccess?: () => void;
}

const FileUpload = ({
  project,
  region,
  type,
  handleClose,
  onUploadSuccess,
}: MonthlyUsageModelProps) => {
  const { t } = useTranslation();
  const { call } = useApiCall();
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorData, setErrorData] = useState<any>(null);
  const site = getLocalStorage(ls_vars.PROJECT_SITE_ID);

  const [sampleEquipmentUsageCsvData] = useState([
    {
      equipment_ref: "",
      month: "",
      year: "",
      no_of_hours: "",
      litres_or_gallons: "",
    },
  ]);

  const [sampleEquipmentCsvData] = useState([
    {
      name: "",
      category: "",
      brand: "",
      model: "",
      fuel_type: "",
      emission_factor: "",
    },
  ]);

  const [downloadLink, setDownloadLink] = useState<string>("");

  // Function to generate CSV and create download link
  const generateCsvLink = () => {
    const headers = Object.keys(type === "equipment" ? sampleEquipmentCsvData[0] : sampleEquipmentUsageCsvData[0]).join(",") + "\n";
    const rows = type === "equipment" ? sampleEquipmentCsvData.map((row) => Object.values(row).join(","))
    .join("\n") : sampleEquipmentUsageCsvData.map((row) => Object.values(row).join(",")).join("\n");

    const csvContent = headers + rows;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    setDownloadLink(url);
  };

  useEffect(() => {
    generateCsvLink();
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      setUploadedFile(file);
    } else {
      console.error("No file was dropped");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleClick = () => {
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement;
    fileInput.click();
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering handleClick
    setUploadedFile(null);
  };

  const handleFileUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (uploadedFile) {
      const formData = new FormData();
      formData.append("file", uploadedFile);
      formData.append("file_name", uploadedFile.name);
      formData.append("file_size", uploadedFile.size.toLocaleString());

      if (type === "equipment") {
        call(
          () => uploadEquipment(formData, region, project, site),
          (res: any) => {
            setUploadedFile(null);
            toast.success(t("Translation.file_upload_success") || "File uploaded successfully!");
            onUploadSuccess?.();
            handleClose?.();
          },
          (error: any) => {
            console.error("Equipment file upload failed:", error);
            const errorResponse = error?.data || error?.response?.data;
            
            if (errorResponse?.errors && errorResponse?.errors.length > 1) {
              setErrorData({
                successCount: errorResponse?.successCount || 0,
                errorCount: errorResponse?.errorCount || errorResponse?.errors.length,
                errors: errorResponse?.errors
              });
              setShowErrorModal(true);
            } else {
              toast.error(errorResponse?.detail || error?.message || t("Translation.file_upload_failed") || "File upload failed!");
            }
          }
        );
      } else {
        call(
          () => uploadEquipmentUsage(formData, region, project, site),
          (res: any) => {
            setUploadedFile(null);
            toast.success(t("Translation.file_upload_success") || "File uploaded successfully!");
            onUploadSuccess?.();
            handleClose?.();
          },
          (error: any) => {
            console.error("Equipment usage file upload failed:", error);
            const errorResponse = error?.data || error?.response?.data;
            
            if (errorResponse?.errors && errorResponse?.errors.length > 1) {
              setErrorData({
                successCount: errorResponse?.successCount || 0,
                errorCount: errorResponse?.errorCount || errorResponse?.errors.length,
                errors: errorResponse?.errors
              });
              setShowErrorModal(true);
            } else {
              toast.error(errorResponse?.detail || error?.message || t("Translation.file_upload_failed") || "File upload failed!");
            }
          }
        );
      }
    }
  };

  return (
    <div className="file-upload-container">
      <form onSubmit={handleFileUpload}>
        <div
          className={`file-input ${
            dragActive ? "border-primary bg-light" : "border-secondary"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            id="file-upload"
            type="file"
            className="d-none"
            onChange={handleFileChange}
            accept=".csv"
          />

          <div className="d-flex flex-column align-items-center text-center w-100">
            <img
              src={images.UploadIcon}
              alt="Upload Icon"
              className="mb-3"
              style={{ height: "3rem", width: "3rem" }}
            />
            <span className="text-muted">
              {t("Translation.drag_and_drop")}
            </span>
            {uploadedFile && (
              <div className="mt-3 d-flex align-items-center justify-content-center">
                <span className="text-primary fw-semibold me-2">
                  {t("Translation.uploaded")}: {uploadedFile.name}
                </span>
                <img
                  src={images.cancel_ic}
                  alt="Cancel Icon"
                  className="cursor-pointer"
                  style={{ height: "1.5rem", width: "1.5rem" }}
                  onClick={handleDelete}
                />
              </div>
            )}
          </div>
        </div>

        {downloadLink && (
          <div className="mt-3 d-flex align-items-center justify-content-center">
            <a
              href={downloadLink}
              download={type === "equipment" ? "Equipment_sample.csv" : "Equipment_usage_sample.csv"}
              className="block mt-4 text-blue-600 underline"
            >
              {t("Translation.download")}: {type === "equipment" ? "Equipment_sample.csv" : "Equipment_usage_sample.csv"}
            </a>
          </div>
        )}

        <div className="mt-3 d-flex justify-content-end">
          <button 
            type="submit" 
            className="btn theme-btn"
            disabled={!uploadedFile}
          >
            {t("Translation.upload")}
          </button>
        </div>
      </form>
      
      {
        errorData && <BulkErrorModal
          open={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          errorData={errorData}
        />
      }
    </div>
  );
};

export default FileUpload;
